import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Grid,
  Typography,
  Autocomplete,
} from '@mui/material';
import { mareOwnersAPI } from '../services/api';
import { US_STATES } from '../constants/usStates';
import { createPrefixFilter } from '../utils/autocompleteFilters';
import { useNotify } from '../context/NotifyContext';
import errorMessage from '../utils/errorMessage';
import useFieldErrors from '../utils/useFieldErrors';

const AddMareOwnerModal = ({ open, onClose, onSuccess }) => {
  const notify = useNotify();
  const fields = useFieldErrors();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    secondary_phone: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    emergency_contact_name: '',
    emergency_contact_phone: '',
    emergency_contact_relationship: '',
    notes: '',
  });

  const handleClose = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      secondary_phone: '',
      address: '',
      city: '',
      state: '',
      zip_code: '',
      emergency_contact_name: '',
      emergency_contact_phone: '',
      emergency_contact_relationship: '',
      notes: '',
    });
    fields.reset();
    onClose();
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    fields.clear(name);
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    if (!fields.validate({ name: !formData.name.trim() && 'Mare owner name is required' })) return;

    setLoading(true);
    try {
      const response = await mareOwnersAPI.create(formData);
      if (onSuccess) {
        onSuccess(response.data);
      }
      handleClose();
    } catch (error) {
      console.error('Error saving mare owner:', error);
      notify.error(errorMessage(error, 'Error saving mare owner'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="md" 
      fullWidth
      sx={{ zIndex: (theme) => theme.zIndex.modal + 2 }}
    >
      <DialogTitle>Add New Mare Owner</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
          <Grid container spacing={2}>
            {/* Basic Information */}
            <Grid item xs={12} sx={{ pb: 0, mb: -0.5 }}>
              <Typography variant="subtitle1" color="primary">
                Contact Information
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                  size="small"
                name="name"
                label="Full Name"
                value={formData.name}
                onChange={handleInputChange}
                {...fields.props('name')}
                required
                fullWidth
                autoFocus
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                  size="small"
                name="email"
                label="Email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                fullWidth
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                  size="small"
                name="phone"
                label="Primary Phone"
                value={formData.phone}
                onChange={handleInputChange}
                fullWidth
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                  size="small"
                name="secondary_phone"
                label="Secondary Phone"
                value={formData.secondary_phone}
                onChange={handleInputChange}
                fullWidth
              />
            </Grid>

            {/* Address */}
            <Grid item xs={12} sx={{ pb: 0, mb: -0.5 }}>
              <Typography variant="subtitle1" color="primary" sx={{ mt: 2 }}>
                Address
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <TextField
                  size="small"
                name="address"
                label="Street Address"
                value={formData.address}
                onChange={handleInputChange}
                fullWidth
              />
            </Grid>

            <Grid item xs={12} sm={5}>
              <TextField
                  size="small"
                name="city"
                label="City"
                value={formData.city}
                onChange={handleInputChange}
                fullWidth
              />
            </Grid>

            <Grid item xs={12} sm={3}>
              <Autocomplete
                options={US_STATES}
                getOptionLabel={(option) => option.name}
                filterOptions={createPrefixFilter((option) => option.name)}
                value={US_STATES.find(state => state.code === formData.state) || null}
                onChange={(event, newValue) => {
                  setFormData((prev) => ({ ...prev, state: newValue ? newValue.code : '' }));
                }}
                renderInput={(params) => (
                  <TextField
                  size="small"
                    {...params}
                    label="State"
                    fullWidth
                  />
                )}
                renderOption={(props, option) => {
                  const { key, ...otherProps } = props;
                  return (
                    <Box component="li" key={key} {...otherProps}>
                      {option.code} - {option.name}
                    </Box>
                  );
                }}
                isOptionEqualToValue={(option, value) => option.code === value?.code}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                  size="small"
                name="zip_code"
                label="Zip Code"
                value={formData.zip_code}
                onChange={handleInputChange}
                fullWidth
              />
            </Grid>

            {/* Emergency Contact */}
            <Grid item xs={12} sx={{ pb: 0, mb: -0.5 }}>
              <Typography variant="subtitle1" color="primary" sx={{ mt: 2 }}>
                Emergency Contact
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                  size="small"
                name="emergency_contact_name"
                label="Emergency Contact Name"
                value={formData.emergency_contact_name}
                onChange={handleInputChange}
                fullWidth
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                  size="small"
                name="emergency_contact_phone"
                label="Emergency Contact Phone"
                value={formData.emergency_contact_phone}
                onChange={handleInputChange}
                fullWidth
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                  size="small"
                name="emergency_contact_relationship"
                label="Relationship"
                value={formData.emergency_contact_relationship}
                onChange={handleInputChange}
                fullWidth
              />
            </Grid>

            {/* Notes */}
            <Grid item xs={12}>
              <TextField
                  size="small"
                name="notes"
                label="Notes"
                value={formData.notes}
                onChange={handleInputChange}
                multiline
                rows={3}
                fullWidth
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading}>
          {loading ? 'Adding...' : 'Add Mare Owner'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddMareOwnerModal;


